import React, { useCallback, useMemo } from "react";

function AnalyticsPage({
  users,
  auctions,
  transactions,
  dateFilteredAuctions,
  dateFilteredTransactions,
  getTransactionAllocation,
}) {
  const getTime = (value) => {
    if (!value) return 0;

    if (value?.seconds) {
      return value.seconds * 1000;
    }

    return Number(value || 0);
  };

  const getTransactionTime = useCallback((transaction) => {
    const value =
      transaction?.createdAt ||
      transaction?.createdAtMs ||
      transaction?.timestamp;

    if (!value) return 0;

    if (value?.seconds) {
      return value.seconds * 1000;
    }

    return Number(value || 0);
  }, []);

  const formatKWD = (value) => {
    return `${Number(value || 0).toFixed(3)} KWD`;
  };

  const formatDateKey = (time) => {
    const date = new Date(time);

    const year = date.getFullYear();
    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");
    const day = String(
      date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const formatMonthKey = (time) => {
    const date = new Date(time);

    const year = date.getFullYear();
    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    return `${year}-${month}`;
  };

  const isToday = (time) => {
    if (!time) return false;

    const today = new Date();
    const date = new Date(time);

    return (
      today.getFullYear() === date.getFullYear() &&
      today.getMonth() === date.getMonth() &&
      today.getDate() === date.getDate()
    );
  };

  const isThisMonth = (time) => {
    if (!time) return false;

    const today = new Date();
    const date = new Date(time);

    return (
      today.getFullYear() === date.getFullYear() &&
      today.getMonth() === date.getMonth()
    );
  };

  const getRevenueFromTransaction = useCallback((transaction) => {
    if (transaction.status !== "succeeded") return 0;

    const amount = Number(transaction.amountKWD || transaction.amount || 0);

    if (amount >= 0) return 0;

    const allocation = getTransactionAllocation(transaction);

    return Number(allocation?.paidImpact || 0);
  }, [getTransactionAllocation]);

  const getPaidInFromTransaction = (transaction) => {
    if (transaction.status !== "succeeded") return 0;

    const amount = Number(transaction.amountKWD || transaction.amount || 0);
    const transactionType = String(transaction.type || "").toLowerCase();

    const isFreeGrant =
      transactionType.includes("promo") ||
      transactionType.includes("refund") ||
      transactionType.includes("admin_credit") ||
      transactionType.includes("admin_refund_credit") ||
      transactionType.includes("admin_promotional_credit") ||
      transactionType.includes("add_balance");

    if (amount <= 0 || isFreeGrant) return 0;

    return amount;
  };

  const buildLastDaysSeries = (days, getValueForDate) => {
    const now = new Date();
    const today = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const result = [];

    for (let index = days - 1; index >= 0; index -= 1) {
      const date = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - index
      );

      const key = formatDateKey(date.getTime());

      result.push({
        label: key.slice(5),
        key,
        value: getValueForDate(key),
      });
    }

    return result;
  };

  const buildLastMonthsSeries = (months, getValueForMonth) => {
    const now = new Date();
    const currentMonth = new Date(
      now.getFullYear(),
      now.getMonth(),
      1
    );
    const result = [];

    for (let index = months - 1; index >= 0; index -= 1) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() - index,
        1
      );

      const key = formatMonthKey(date.getTime());

      result.push({
        label: key,
        key,
        value: getValueForMonth(key),
      });
    }

    return result;
  };

  const analytics = useMemo(() => {
    const totalRevenue = transactions.reduce(
      (sum, transaction) => sum + getRevenueFromTransaction(transaction),
      0
    );

    const totalPaidIn = transactions.reduce(
      (sum, transaction) => sum + getPaidInFromTransaction(transaction),
      0
    );

    const filteredRevenue = dateFilteredTransactions.reduce(
      (sum, transaction) => sum + getRevenueFromTransaction(transaction),
      0
    );

    const filteredPaidIn = dateFilteredTransactions.reduce(
      (sum, transaction) => sum + getPaidInFromTransaction(transaction),
      0
    );

    const revenueToday = transactions.reduce((sum, transaction) => {
      const time = getTransactionTime(
        transaction
      );

      if (!isToday(time)) return sum;

      return sum + getRevenueFromTransaction(transaction);
    }, 0);

    const revenueThisMonth = transactions.reduce((sum, transaction) => {
      const time = getTransactionTime(
        transaction
      );

      if (!isThisMonth(time)) return sum;

      return sum + getRevenueFromTransaction(transaction);
    }, 0);

    const newUsersToday = users.filter((user) =>
      isToday(getTime(user.createdAt || user.joinedAt))
    ).length;

    const newUsersThisMonth = users.filter((user) =>
      isThisMonth(getTime(user.createdAt || user.joinedAt))
    ).length;

    const auctionsToday = auctions.filter((auction) =>
      isToday(getTime(auction.createdAt))
    ).length;

    const auctionsThisMonth = auctions.filter((auction) =>
      isThisMonth(getTime(auction.createdAt))
    ).length;

    const activeSellerKeys = new Set();

    dateFilteredAuctions.forEach((auction) => {
      const sellerKey =
        auction.createdBy ||
        auction.createdByUsername ||
        "";

      if (sellerKey) {
        activeSellerKeys.add(sellerKey);
      }
    });

    const activeBuyerKeys = new Set();

    dateFilteredAuctions.forEach((auction) => {
      const buyerKey =
        auction.currentWinner ||
        auction.winner ||
        "";

      if (buyerKey) {
        activeBuyerKeys.add(buyerKey);
      }
    });

    const totalBids = dateFilteredAuctions.reduce((sum, auction) => {
      return (
        sum +
        Number(
          auction.bidHistory?.length ||
            auction.bids?.length ||
            0
        )
      );
    }, 0);

    const averageRevenuePerAuction =
      dateFilteredAuctions.length > 0
        ? filteredRevenue / dateFilteredAuctions.length
        : 0;

    const revenueByDay = {};

    transactions.forEach((transaction) => {
      const time = getTransactionTime(
        transaction
      );

      if (!time) return;

      const key = formatDateKey(time);

      revenueByDay[key] =
        Number(revenueByDay[key] || 0) +
        getRevenueFromTransaction(transaction);
    });

    const usersByDay = {};

    users.forEach((user) => {
      const time = getTime(user.createdAt || user.joinedAt);

      if (!time) return;

      const key = formatDateKey(time);

      usersByDay[key] = Number(usersByDay[key] || 0) + 1;
    });

    const auctionsByDay = {};

    auctions.forEach((auction) => {
      const time = getTime(auction.createdAt);

      if (!time) return;

      const key = formatDateKey(time);

      auctionsByDay[key] = Number(auctionsByDay[key] || 0) + 1;
    });

    const revenueByMonth = {};

    transactions.forEach((transaction) => {
      const time = getTransactionTime(
        transaction
      );

      if (!time) return;

      const key = formatMonthKey(time);

      revenueByMonth[key] =
        Number(revenueByMonth[key] || 0) +
        getRevenueFromTransaction(transaction);
    });

    const auctionsByMonth = {};

    auctions.forEach((auction) => {
      const time = getTime(auction.createdAt);

      if (!time) return;

      const key = formatMonthKey(time);

      auctionsByMonth[key] = Number(auctionsByMonth[key] || 0) + 1;
    });

    const winnerDeclaredAuctions = dateFilteredAuctions.filter(
      (auction) => auction.closureReason === "winner_declared"
    ).length;

    const closedAuctions = dateFilteredAuctions.filter(
      (auction) => auction.status === "closed"
    ).length;

    const auctionSuccessRate =
      closedAuctions > 0
        ? (winnerDeclaredAuctions / closedAuctions) * 100
        : 0;

    const categoryPerformanceMap = {};

    dateFilteredAuctions.forEach((auction) => {
      const category = auction.category || "Uncategorized";

      if (!categoryPerformanceMap[category]) {
        categoryPerformanceMap[category] = {
          category,
          auctions: 0,
          revenue: 0,
        };
      }

      categoryPerformanceMap[category].auctions += 1;
      categoryPerformanceMap[category].revenue +=
        Number(auction.billingAmountKWD || 0) +
        Number(auction.extensionTotalAmountKWD || 0);
    });

    const categoryPerformanceRows = Object.values(categoryPerformanceMap).sort(
      (a, b) => Number(b.revenue || 0) - Number(a.revenue || 0)
    );

    return {
      totalRevenue,
      totalPaidIn,
      filteredRevenue,
      filteredPaidIn,
      revenueToday,
      revenueThisMonth,
      newUsersToday,
      newUsersThisMonth,
      auctionsToday,
      auctionsThisMonth,
      activeSellers: activeSellerKeys.size,
      activeBuyers: activeBuyerKeys.size,
      totalBids,
      averageRevenuePerAuction,
      revenueByDay,
      usersByDay,
      auctionsByDay,
      revenueByMonth,
      auctionsByMonth,
      winnerDeclaredAuctions,
      closedAuctions,
      auctionSuccessRate,
      categoryPerformanceRows,
    };
  }, [
    users,
    auctions,
    transactions,
    dateFilteredAuctions,
    dateFilteredTransactions,
    getRevenueFromTransaction,
    getTransactionTime,
  ]);

  const revenueDailySeries = buildLastDaysSeries(14, (key) =>
    Number(analytics.revenueByDay[key] || 0)
  );

  const userDailySeries = buildLastDaysSeries(14, (key) =>
    Number(analytics.usersByDay[key] || 0)
  );

  const auctionDailySeries = buildLastDaysSeries(14, (key) =>
    Number(analytics.auctionsByDay[key] || 0)
  );

  const revenueMonthlySeries = buildLastMonthsSeries(6, (key) =>
    Number(analytics.revenueByMonth[key] || 0)
  );

  const auctionMonthlySeries = buildLastMonthsSeries(6, (key) =>
    Number(analytics.auctionsByMonth[key] || 0)
  );

  const renderBarChart = (title, description, data, valueFormatter) => {
    const maxValue = Math.max(...data.map((item) => Number(item.value || 0)), 1);

    return (
      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>{title}</h2>
            <p>{description}</p>
          </div>
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          {data.map((item) => {
            const width = Math.max(
              (Number(item.value || 0) / maxValue) * 100,
              Number(item.value || 0) > 0 ? 4 : 0
            );

            return (
              <div
                key={item.key}
                style={{
                  display: "grid",
                  gridTemplateColumns: "90px 1fr 110px",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <span className="small-muted">{item.label}</span>

                <div
                  style={{
                    height: 14,
                    borderRadius: 999,
                    background: "#e5e7eb",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${width}%`,
                      borderRadius: 999,
                      background: "#2563eb",
                    }}
                  />
                </div>

                <strong style={{ fontSize: 13 }}>
                  {valueFormatter(item.value)}
                </strong>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  return (
    <>
      <section className="stats-grid">
        <div className="stat-card">
          <span>Revenue Today</span>
          <strong>{formatKWD(analytics.revenueToday)}</strong>
        </div>

        <div className="stat-card">
          <span>Revenue This Month</span>
          <strong>{formatKWD(analytics.revenueThisMonth)}</strong>
        </div>

        <div className="stat-card">
          <span>Total Revenue</span>
          <strong>{formatKWD(analytics.totalRevenue)}</strong>
        </div>

        <div className="stat-card">
          <span>Total Paid In</span>
          <strong>{formatKWD(analytics.totalPaidIn)}</strong>
        </div>

        <div className="stat-card">
          <span>Filtered Revenue</span>
          <strong>{formatKWD(analytics.filteredRevenue)}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Based on Global Date Range
          </small>
        </div>

        <div className="stat-card">
          <span>Filtered Paid In</span>
          <strong>{formatKWD(analytics.filteredPaidIn)}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Based on Global Date Range
          </small>
        </div>

        <div className="stat-card">
          <span>New Users Today</span>
          <strong>{analytics.newUsersToday}</strong>
        </div>

        <div className="stat-card">
          <span>New Users This Month</span>
          <strong>{analytics.newUsersThisMonth}</strong>
        </div>

        <div className="stat-card">
          <span>Auctions Today</span>
          <strong>{analytics.auctionsToday}</strong>
        </div>

        <div className="stat-card">
          <span>Auctions This Month</span>
          <strong>{analytics.auctionsThisMonth}</strong>
        </div>

        <div className="stat-card">
          <span>Active Sellers</span>
          <strong>{analytics.activeSellers}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Based on Global Date Range
          </small>
        </div>

        <div className="stat-card">
          <span>Unique Winning Buyers</span>
          <strong>{analytics.activeBuyers}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Current or declared winners in the Global Date Range
          </small>
        </div>

        <div className="stat-card">
          <span>Auction Success Rate</span>
          <strong>{Number(analytics.auctionSuccessRate || 0).toFixed(1)}%</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Winner declared / closed auctions
          </small>
        </div>

        <div className="stat-card">
          <span>Successful Auctions</span>
          <strong>{analytics.winnerDeclaredAuctions}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Closed with winner declared
          </small>
        </div>

        <div className="stat-card">
          <span>Closed Auctions</span>
          <strong>{analytics.closedAuctions}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Based on Global Date Range
          </small>
        </div>
      </section>

      <section className="table-card">
      <div className="table-header">
          <div>
            <h2>Category KPI Summary</h2>
            <p>Auction billing and auction volume by category, based on the Global Date Range.</p>
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
            <tr>
                <th>Category</th>
                <th>Auctions</th>
                <th>Auction Billing</th>
              </tr>
            </thead>

            <tbody>
              {analytics.categoryPerformanceRows.map((row) => (
                <tr key={row.category}>
                  <td>{row.category}</td>
                  <td>{row.auctions}</td>
                  <td>{formatKWD(row.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {renderBarChart(
        "Revenue by Day",
        "Paid revenue consumed during the last 14 days.",
        revenueDailySeries,
        formatKWD
      )}

      {renderBarChart(
        "Revenue by Month",
        "Paid revenue consumed during the last 6 months.",
        revenueMonthlySeries,
        formatKWD
      )}

{renderBarChart(
        "Auctions by Month",
        "Auctions created during the last 6 months.",
        auctionMonthlySeries,
        (value) => Number(value || 0)
      )}

      {renderBarChart(
        "New Users by Day",
        "New user records created during the last 14 days.",
        userDailySeries,
        (value) => Number(value || 0)
      )}

      {renderBarChart(
        "New Auctions by Day",
        "Auctions created during the last 14 days.",
        auctionDailySeries,
        (value) => Number(value || 0)
      )}

      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>Business Summary</h2>
            <p>High-level owner metrics for the current platform state.</p>
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Value</th>
                <th>Meaning</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>Total Users</td>
                <td>{users.length}</td>
                <td>All user records currently loaded from Firestore.</td>
              </tr>

              <tr>
                <td>Total Auctions</td>
                <td>{auctions.length}</td>
                <td>All auction records currently loaded from Firestore.</td>
              </tr>

              <tr>
                <td>Filtered Auctions</td>
                <td>{dateFilteredAuctions.length}</td>
                <td>Auctions matching the current Global Date Range.</td>
              </tr>

              <tr>
                <td>Filtered Transactions</td>
                <td>{dateFilteredTransactions.length}</td>
                <td>Transactions matching the current Global Date Range.</td>
              </tr>

              <tr>
                <td>Total Bids</td>
                <td>{analytics.totalBids}</td>
                <td>Actual bids recorded in available auction bid history.</td>
              </tr>

              <tr>
                <td>Average Revenue Per Filtered Auction</td>
                <td>{formatKWD(analytics.averageRevenuePerAuction)}</td>
                <td>Filtered revenue divided by filtered auction count.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

export default AnalyticsPage;